import { useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react';

import useTranslations from '@/i18n/useTranslations';
import SearchNavbarIcon from '@/shared-components/icons/components/navigation/SearchNavbarIcon';
import SearchField from '@/shared-components/inputs/components/SearchField';

import './styles/Searchbar.css';

export default function Searchbar({ query, setQuery, categorySelected, activeCategory }) {
  const t = useTranslations('common.shared.search');
  const categories = useMemo(
    () => ['Artworks', 'Galleries', 'Artists', 'Users', 'Artlists'],
    []
  );
  const defaultCategory = categories[0];
  const selected = activeCategory || defaultCategory;
  const containerRef = useRef(null);
  const itemRefs = useRef([]);
  const [indicatorStyle, setIndicatorStyle] = useState({ width: 0, left: 0 });

  const updateIndicator = useCallback(() => {
    const containerElement = containerRef.current;
    if (!containerElement) {
      return;
    }

    const activeIndex = Math.max(0, categories.indexOf(selected));
    const activeElement = itemRefs.current[activeIndex];

    if (!activeElement) {
      return;
    }

    const containerRect = containerElement.getBoundingClientRect();
    const activeRect = activeElement.getBoundingClientRect();
    const width = activeRect.width;
    const left = activeRect.left - containerRect.left;

    setIndicatorStyle((prev) => {
      if (prev.width === width && prev.left === left) {
        return prev;
      }

      return { width, left };
    });
  }, [categories, selected]);

  useLayoutEffect(() => {
    updateIndicator();
  }, [updateIndicator]);

  useLayoutEffect(() => {
    const handleResize = () => {
      updateIndicator();
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [updateIndicator]);

  useLayoutEffect(() => {
    const activeIndex = Math.max(0, categories.indexOf(selected));
    const activeElement = itemRefs.current[activeIndex];

    activeElement?.scrollIntoView({ block: 'nearest', inline: 'center' });
  }, [categories, selected]);

  const handleCategoryClick = (category) => {
    categorySelected(category);
  };

  const getCategoryLabel = useCallback((category) => {
    if (category === 'Artlists') {
      // Product requirement: keep "Artlists" label in English.
      return category;
    }

    const map = {
      Artworks: t('categories.artworks'),
      Galleries: t('categories.galleries'),
      Artists: t('categories.artists'),
      Users: t('categories.users'),
    };

    return map[category] ?? category;
  }, [t]);

  return (
    <>
      <SearchField
        className="searchbar__container"
        leading={<SearchNavbarIcon />}
        value={query.q || ''}
        onInput={(e) => setQuery({ ...query, q: e.target.value })}
        onClear={() => setQuery({ ...query, q: '' })}
        placeholder={t('searchbar.placeholder')}
        spellCheck={false}
        autoComplete="off"
      />
      <div className="search__group__sections" role="tablist">
        <div className="search__group__rail" ref={containerRef}>
          {categories.map((item, index) => (
            <button
              key={item}
              type="button"
              ref={(element) => {
                itemRefs.current[index] = element;
                if (element) {
                  updateIndicator();
                }
              }}
              className={`search__group__item ${selected === item ? 'selected' : ''}`}
              onClick={() => handleCategoryClick(item)}
              role="tab"
              aria-selected={selected === item}
            >
              {getCategoryLabel(item)}
            </button>
          ))}
          <span
            className="search__group__active-indicator"
            style={{
              width: indicatorStyle.width,
              transform: `translateX(${indicatorStyle.left}px)`
            }}
            aria-hidden="true"
          />
        </div>
      </div>
    </>
  );
}
